import * as React from 'react';

import { useQuery } from '@apollo/client';

import { getLanguageCode } from 'js/lib/language';
import { useParams } from 'js/lib/useRouter';
import user from 'js/lib/user';

import type { StyleDeclarationValue } from '@coursera/aphrodite';
import { Grid, Typography } from '@coursera/cds-core';
import { StyleSheet, breakPoint, css } from '@coursera/coursera-ui';

import { COLLECTION_CAROUSEL_MAX_WIDTH, SCREEN_TABLET } from 'bundles/browse/constants';
import ProductCardCollection from 'bundles/collections-common/components/ProductCardCollection';
import ProductCardCollectionPlaceholder from 'bundles/collections-common/components/ProductCardCollectionPlaceholder';
import { getCollectionItems } from 'bundles/collections/utils/catalogCollectionPageUtils';
import { mapCatalogCollectionToCarouselCollection } from 'bundles/coursera-plus-landing-page/courseraPlusCollections/utils';
import { CATALOG_COLLECTION_PAGE_QUERY } from 'bundles/coursera-plus-landing-page/queries';
import type {
  CatalogCollectionPageQuery,
  CatalogCollectionPageQueryVariables,
} from 'bundles/coursera-plus-landing-page/queries/__generated__/CatalogCollectionPageQuery';
import { DEFAULT_COLLECTION_SLUG } from 'bundles/coursera-plus/components/constants';
import { isGenerativeAILandingPage } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import { BASE_STYLES } from 'bundles/design-system/components/container-styles';
import BrowseProductCard from 'bundles/payments-common/components/BrowseProductCard';
import Collection, { CollectionPlaceholder } from 'bundles/payments-common/components/LegacyCollection';
import inServerContext from 'bundles/ssr/util/inServerContext';

import _t from 'i18n!nls/coursera-plus-landing-page';

const styles = StyleSheet.create({
  containerWrapper: {
    ...BASE_STYLES.containerWrapper,
    background: '#F4EDDF',
  },
  container: {
    ...BASE_STYLES.container,
    padding: '64px 0 82px',
    margin: '0 auto',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      padding: '56px 0 42px',
    },
    [`@media (max-width: ${breakPoint.sm - 1}px)`]: {
      padding: '48px 0 40px',
    },
  },
  collectionWrapper: {
    position: 'relative',
    [`@media (max-width: ${COLLECTION_CAROUSEL_MAX_WIDTH}px)`]: {
      padding: '0 24px',
    },
    // collection carousel's breakpoint is SCREEN_TABLET - 1
    [`@media (max-width: ${SCREEN_TABLET - 1}px)`]: {
      padding: '0',
    },
  },
  titleWrapper: {
    textAlign: 'center',
    justifyContent: 'center',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      justifyContent: 'left',
    },
  },
  title: {
    ...BASE_STYLES.title,
    marginBottom: '10px',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      ...BASE_STYLES.titleMobile,
      marginBottom: '-10px',
    },
  },
});

export type PropsFromCaller = {
  collectionSlug?: string;
  additionalCollectionItemProps?: Record<string, boolean | string>;
  productSectionBackgroundStyles?: StyleDeclarationValue;
  titleOverride?: string;
  isDefaultLandingPage?: boolean;
};

const CourseraPlusCollection = (props: PropsFromCaller) => {
  const {
    collectionSlug,
    additionalCollectionItemProps,
    productSectionBackgroundStyles,
    titleOverride,
    isDefaultLandingPage,
  } = props;

  const { data } = useQuery<CatalogCollectionPageQuery, CatalogCollectionPageQueryVariables>(
    CATALOG_COLLECTION_PAGE_QUERY,
    {
      skip: inServerContext,
      variables: {
        slug: collectionSlug ?? DEFAULT_COLLECTION_SLUG,
      },
    }
  );
  const catalogCollectionPage = data?.CatalogCollectionPagesV1Resource?.bySlug?.elements?.[0];
  const { slug } = useParams();
  const getSectionTitle = () => {
    if (isGenerativeAILandingPage(slug)) {
      return _t('Learn the fundamentals of Generative AI');
    }
    return _t('See what you can learn with Coursera Plus');
  };
  const sectionTitle = getSectionTitle();

  const renderCarousel = () => {
    if (isDefaultLandingPage) {
      // the collection is only retrieved when not in server context
      if (catalogCollectionPage) {
        return <ProductCardCollection collection={mapCatalogCollectionToCarouselCollection(catalogCollectionPage)} />;
      } else {
        return <ProductCardCollectionPlaceholder hideTitle={true} />;
      }
    }
    // the collection is only retrieved when not in server context
    if (catalogCollectionPage) {
      let collectionItems;
      const { collection, s12ns, courses } = catalogCollectionPage;
      const s12nsArray = s12ns?.elements ?? [];
      const coursesArray = courses?.elements ?? [];
      collectionItems = getCollectionItems({ collection, s12nsArray, coursesArray });
      if (additionalCollectionItemProps) {
        collectionItems = collectionItems.map((item) => ({ ...item, ...additionalCollectionItemProps }));
      }
      const trackingData = {
        userId: user.isAuthenticatedUser() ? user.get().id : null,
        languageCode: getLanguageCode(),
      };

      if (collectionItems) {
        return (
          <Collection
            tagClassNames="horizontal-box align-items-vertical-center"
            trackingData={trackingData}
            // @ts-expect-error ts-migrate(2322) FIXME: Type '(((BaseOnDemandSpecializationsV1 | BaseCours... Remove this comment to see the full error message
            collection={collectionItems}
            CollectionItem={BrowseProductCard}
            collectionName={sectionTitle}
            hideCollectionNameHeader={true}
          />
        );
      }
    }

    return <CollectionPlaceholder hideCollectionNameHeader={true} />;
  };

  return (
    <div {...css(styles.containerWrapper, productSectionBackgroundStyles)}>
      <Grid className={css(styles.container).className}>
        <Grid className={css(styles.titleWrapper).className}>
          <Typography variant="d2semibold" className={css(styles.title).className}>
            {titleOverride ?? sectionTitle}
          </Typography>
        </Grid>
        <Grid xs={12}>
          <div {...css(styles.collectionWrapper)}>{renderCarousel()}</div>
        </Grid>
      </Grid>
    </div>
  );
};

export default CourseraPlusCollection;
